export interface MeasureDefinition {
  displayName: string;
  summary: string;
  eligibility: string;
  metWhen: string;
  exclusions?: string[];
  tips?: string;
  lowerIsBetter?: boolean;
  /** Labels for the three stat tiles: [eligible, met, remaining] */
  statLabels: [string, string, string];
  /** Clinical messaging shown when the measure has 0 eligible patients (zero-denominator) */
  zeroDenomMessage?: string;
  /** Placeholder for future links to detailed measure information */
  learnMoreUrl?: string;
}

export const MEASURE_DEFINITIONS: Record<string, MeasureDefinition> = {
  AWV: {
    displayName: "Annual Wellness Visit",
    summary:
      "Tracks completion of the Medicare Annual Wellness Visit — a comprehensive preventive care encounter that includes health risk assessment, care plan updates, and screening referrals.",
    eligibility:
      "Medicare patients attributed to this provider who are due for their AWV in the current quarter based on their eligible month. Patients who are deceased, not enrolled in primary care, discharged, in ISNP, or in POS 31 are excluded from the denominator.",
    metWhen:
      "AWV is completed on or after the patient's eligible month.",
    tips: "Scheduling AWVs early in the quarter gives more time for completion. Prioritize patients whose eligible month falls earliest in the quarter.",
    statLabels: ["Eligible Patients", "AWV Completed", "Still Needed"],
    zeroDenomMessage:
      "None of your attributed patients are due for their Annual Wellness Visit this quarter. This measure is not affecting your score.",
  },
  A1C: {
    displayName: "Diabetes A1C Control",
    summary:
      "Measures whether patients with diabetes have had their hemoglobin A1C tested and documented, ensuring ongoing monitoring of blood sugar control.",
    eligibility:
      "Patients ages 18–75 with a diabetes diagnosis attributed to this provider.",
    metWhen:
      "A1C lab result is documented within the measurement period. Consolidated A1C values from external labs are also accepted.",
    exclusions: [
      "Patients without a confirmed diabetes diagnosis",
      "Hospice or palliative care patients",
      "Patients age 66+ with frailty and advanced illness",
    ],
    statLabels: ["Diabetic Patients", "A1C Documented", "Missing A1C"],
    zeroDenomMessage:
      "None of your attributed patients currently have an active diabetes diagnosis requiring A1C monitoring. This measure is not affecting your score.",
  },
  BCS: {
    displayName: "Breast Cancer Screening",
    summary:
      "Tracks whether age-eligible women have received a mammogram within the recommended screening interval, supporting early detection of breast cancer.",
    eligibility: "Women ages 40–74 attributed to this provider.",
    metWhen:
      "A mammogram is documented within the past 2 years. Claims data, lab results, counseling documentation, and progress note documentation are all accepted.",
    exclusions: [
      "Women under 40 or over 74",
      "Patients with bilateral mastectomy",
    ],
    statLabels: ["Eligible Women", "Screening Complete", "Not Screened"],
    zeroDenomMessage:
      "None of your attributed patients meet the age and gender criteria for breast cancer screening. This measure is not affecting your score.",
  },
  CRCS: {
    displayName: "Colorectal Cancer Screening",
    summary:
      "Ensures age-eligible patients are up to date on colorectal cancer screening through any of several accepted methods.",
    eligibility: "Patients ages 45–75 attributed to this provider.",
    metWhen:
      "Any of: colonoscopy within 10 years, FIT/FOBT within 1 year, FIT-DNA (Cologuard) within 3 years, or CT colonography within 5 years.",
    exclusions: [
      "Patients under 45 or over 75",
      "Patients with total colectomy",
      "Patients with a history of colorectal cancer",
    ],
    statLabels: ["Eligible Patients", "Screening Current", "Overdue"],
    zeroDenomMessage:
      "None of your attributed patients meet the age criteria for colorectal cancer screening. This measure is not affecting your score.",
  },
  PHQ9: {
    displayName: "Depression Screening (PHQ-9)",
    summary:
      "Measures completion of a standardized depression screening assessment, enabling early identification and intervention for depression.",
    eligibility:
      "Patients age 12 and older without a bipolar disorder diagnosis. If psychiatry services are active, the psychiatry provider is responsible; otherwise, the PCP is responsible.",
    metWhen:
      "A PHQ-9 screening is completed and scored within the measurement period. Both PCP-side and psychiatry-side screenings count toward completion.",
    exclusions: [
      "Patients with a bipolar disorder diagnosis",
    ],
    tips: "Check the PCP Met and Psych Met columns — some patients only need one side completed to close the gap.",
    statLabels: ["Eligible Patients", "PHQ-9 Complete", "Not Screened"],
    zeroDenomMessage:
      "None of your attributed patients currently meet the criteria for depression screening. This measure is not affecting your score.",
  },
  HTN: {
    displayName: "Hypertension Control",
    summary:
      "Tracks whether patients with hypertension have a most recent blood pressure reading that meets the controlled threshold, indicating effective management.",
    eligibility:
      "Patients ages 18–85 with a hypertension diagnosis attributed to this provider.",
    metWhen:
      "Most recent blood pressure reading is below 140/90 mmHg as documented in the chart.",
    exclusions: [
      "Patients without a confirmed hypertension diagnosis",
      "Patients with end-stage renal disease on dialysis",
      "Pregnant patients",
      "Patients age 66+ with frailty and advanced illness",
    ],
    tips: "Focus on patients whose last BP was close to threshold — a single controlled reading flips their status.",
    statLabels: ["HTN Patients", "BP Controlled", "Not Controlled"],
    zeroDenomMessage:
      "None of your attributed patients have an active hypertension diagnosis requiring blood pressure monitoring. This measure is not affecting your score.",
  },
  ACP: {
    displayName: "Advance Care Planning",
    summary:
      "Ensures eligible patients have a documented advance care planning conversation, supporting patient autonomy and end-of-life care preferences.",
    eligibility: "Patients age 65 and older attributed to this provider.",
    metWhen:
      "An ACP conversation is documented with CPT code 99497 or 99498, or quality codes 1123F/1124F are present in the chart.",
    tips: "ACP conversations can happen during AWV visits — pairing them is an efficient way to close both measures.",
    statLabels: ["Patients 65+", "ACP Documented", "No ACP"],
    zeroDenomMessage:
      "None of your attributed patients meet the age criteria for advance care planning documentation. This measure is not affecting your score.",
  },
  ACO_Visits: {
    displayName: "ACO Patient Visits",
    summary:
      "Measures the percentage of ACO-attributed patients who have been seen by this provider in the current calendar year. This is an eligibility gate for PC Physicians — if unmet, the total quarterly award is $0. It is not a weighted outcome measure.",
    eligibility:
      "PC Physician only. All patients on this provider's ACO attribution panel for the current year.",
    metWhen:
      "Patient has been seen by this provider in the current calendar year.",
    tips: "This measure tracks calendar-year visits, not quarterly. Early-year outreach to unvisited patients has the biggest impact.",
    statLabels: ["ACO Patients", "Visited This Year", "Not Yet Seen"],
    zeroDenomMessage:
      "You have no ACO-attributed patients for the current year. This measure is not affecting your score.",
  },
  ED_SendOut: {
    displayName: "ED Send-Out Rate",
    summary:
      "Tracks the rate of teletriage encounters that result in the patient being routed to the Emergency Department. Lower rates indicate better triage and care coordination.",
    eligibility:
      "Teletriage encounters classified as ED risk. Excludes encounters with no ED risk, controlled refills, and controlled medication new admits.",
    metWhen:
      "The encounter did NOT result in an ED referral. This is an inverse measure — lower ED send-out rates are better.",
    lowerIsBetter: true,
    statLabels: ["ED Risk Encounters", "Resolved In-House", "Sent to ED"],
    zeroDenomMessage:
      "No teletriage encounters have been assigned to you. This measure is not affecting your score.",
  },
  PIMs: {
    displayName: "Medication Safety (PIMs)",
    summary:
      "PIMs is a monthly pharmacist audit on all active patients in the EHR. Accurate audits require an up-to-date medication list in the patient's chart.",
    eligibility:
      "Patients with active medication lists at facilities assigned to this provider, selected for pharmacist review.",
    metWhen:
      "Performance is derived from average medication complexity scores across all audited patients at your assigned facilities.",
    tips: "Click on any patient to see complete audit details for the most recent month.",
    statLabels: ["Patients Audited", "Avg Complexity", "Target Score"],
    zeroDenomMessage:
      "No patients have been selected for pharmacist medication review at your facilities. This measure is not affecting your score.",
  },
  Coding: {
    displayName: "Coding Accuracy",
    summary:
      "Measures documentation accuracy from audited clinical encounters, ensuring proper medical coding supports quality reporting and reimbursement.",
    eligibility:
      "Clinical encounters randomly selected for coding audit during the quarter.",
    metWhen:
      "Each audited encounter is scored on coding accuracy. The measure performance is the average score across all audits.",
    statLabels: ["Audits Completed", "Passing (\u226590%)", "Below Threshold"],
    zeroDenomMessage:
      "No clinical encounters have been selected for coding audit this quarter. This measure is not affecting your score.",
  },
  Quality_Audit: {
    displayName: "Signature Standards",
    summary:
      "Measures adherence to Eventus Signature Standards in clinical documentation — the organization's benchmark for quality in patient encounters.",
    eligibility:
      "Clinical encounters randomly selected for quality audit during the quarter.",
    metWhen:
      "Each audited encounter is scored against Signature Standards criteria. The measure performance is the average score across all audits.",
    statLabels: ["Audits Completed", "Passing (\u226590%)", "Below Threshold"],
    zeroDenomMessage:
      "No clinical encounters have been selected for documentation quality audit this quarter. This measure is not affecting your score.",
  },
};
